import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { TWarehouseTaskUpdateReq } from 'api/swagger';
import { useUpdateWarehouseTask } from 'api/warehouse-task/use-update-warehouse-task';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import { formatDate } from 'shared/utils/time/format-date';
import { formatWhTaskDates } from 'shared/utils/wh-tasks/format-wh-task-dates';
import { WHTaskType } from 'types/wh-task';
import {
  EFormDrawerSize,
  EFormDrawerWithButtonIconType,
  FormDrawerWithButton,
} from 'components/common/form-drawer-with-button';
import type { IWhTaskFormFields } from 'components/wh-tasks/wh-task-form/types';
import { WhTaskForm } from '../wh-task-form';
import type { IWhTaskEdit } from './types';

export const WhTaskEdit = ({ whTask }: IWhTaskEdit) => {
  const isAllow = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.customer_manager]);
  const { t } = useTranslation();
  const { mutateAsync: updateWhTask, isLoading } = useUpdateWarehouseTask(whTask.id);

  const handleFinish = async (values: IWhTaskFormFields) => {
    const { arrivalDate, departureDate } = formatWhTaskDates(values.date);
    const isReloading = values.type === WHTaskType.reloading;

    const apiVariables: TWarehouseTaskUpdateReq = {
      type: values.type,
      warehouseId: values.warehouseId,
      contactId: values.contact.value,
      clientOrderNumber: values.clientOrderNumber,
      currency: values.currency,
      freight: values.freight,
      fineToUs: values.fineToUs,
      fineByUs: values.fineByUs,
      totalWeight: values.totalWeight,
      arrivalDate,
      departureDate,
      additionalInfo: values.additionalInfo,
      plateNumberFrom: isReloading ? values.plateNumberFrom : null,
      plateNumberTo: isReloading ? values.plateNumberTo : null,
      realArrivalDate: values.realArrivalDate ? formatDate(values.realArrivalDate) : null,
      realDepartureDate: values.realDepartureDate ? formatDate(values.realDepartureDate) : null,
    };

    await updateWhTask(apiVariables);
  };

  const isReloading = whTask.type === WHTaskType.reloading;

  const initialValues = {
    type: whTask.type,
    client: {
      value: whTask.client.id,
      label: whTask.client.shortName ?? whTask.client.name,
    },
    contact: {
      value: whTask.contact.id,
      label: whTask.contact.name,
    },
    clientOrderNumber: whTask.clientOrderNumber,
    warehouseId: whTask.warehouseId,
    ...(!isReloading
      ? {
          date: [dayjs(whTask.arrivalDate), dayjs(whTask.departureDate)] as [Dayjs, Dayjs],
        }
      : {
          date: dayjs(whTask.arrivalDate),
        }),
    plateNumberFrom: whTask.plateNumberFrom,
    plateNumberTo: whTask.plateNumberTo,
    totalWeight: whTask.totalWeight,
    currency: whTask.currency,
    freight: whTask.freight,
    fineToUs: whTask.fineToUs,
    fineByUs: whTask.fineByUs,
    additionalInfo: whTask.additionalInfo,
    realArrivalDate: whTask.realArrivalDate ? dayjs(whTask.realArrivalDate) : undefined,
    realDepartureDate: whTask.realDepartureDate ? dayjs(whTask.realDepartureDate) : undefined,
    warehouseTaskItems: [],
  };

  if (!isAllow) return null;

  return (
    <FormDrawerWithButton
      formComponent={WhTaskForm}
      formProps={{
        initialValues,
        withFines: true,
        withoutWarehouseTaskItems: true,
        disableClientInput: true,
        withRealDates: true,
      }}
      title={t('wh-task:wh_task_edit')}
      buttonLabel={t('wh-task:wh_task_edit')}
      iconType={EFormDrawerWithButtonIconType.EDIT}
      size={EFormDrawerSize.LARGE}
      isLoading={isLoading}
      onFinish={handleFinish}
    />
  );
};
